import { Button, Flex, Space, Table, Tooltip } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeartLoader from '@/shared/ui/HeartLoader';
import { formatStudyDateTime } from '@/shared/utils/studyDate';
import type { Study } from '@/models/study';
import { getColorFromReportStatus, sorterToOrdering } from 'services/utils.ts';
import { useSettings } from '@/features/settings';
import { FaRegClipboard } from 'react-icons/fa';
import { useStudyPrefetch } from '@/state/hooks/useStudyPrefetch';
import { IoEyeOutline } from 'react-icons/io5';
import { StudyTagsCell } from './StudyTagsCell';
import StudyPropertiesModal from '../study-detail/StudyPropertiesModal/StudyPropertiesModal';
import tableStyles from './StudyTable.module.css';

interface Props {
  studies: Study[];
  loading?: boolean;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSortChange?: (ordering: string | undefined) => void;
  onStudyClick?: (uuid: string) => void;
}

const StudyTable: FC<Props> = ({
  studies,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onStudyClick,
}) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const prefetchStudy = useStudyPrefetch();

  const columns = [
    {
      title: 'Patient',
      dataIndex: 'patientFirstName',
      key: 'patientFirstName',
      sorter: true,
      render: (text: string, record: Study) => (
        <Flex
          gap="large"
          align="middle"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setSelectedStudy(record);
            setPropertiesOpen(true);
          }}
        >
          <FaRegClipboard
            color={getColorFromReportStatus(record.reportStatus ?? '')}
            size={18}
          />
          <span style={{ textDecoration: 'underline' }}>
            {`${text} ${record.patientLastName}`}
          </span>
        </Flex>
      ),
    },
    {
      title: 'MRN',
      dataIndex: 'patientMedicalRecordNumber',
      key: 'patientMedicalRecordNumber',
      sorter: true,
    },
    {
      title: 'Study Type',
      dataIndex: 'modality',
      key: 'modality',
      sorter: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      sorter: true,
      render: (priority: string | null) => {
        if (!priority) return '-';
        const icon = { STAT: '▲', URGENT: '●', ROUTINE: '▼' }[priority] ?? '▼';
        return (
          <span className={tableStyles.priorityPill} data-priority={priority}>
            {icon} {priority}
          </span>
        );
      },
    },
    {
      title: 'Report Status',
      dataIndex: 'reportStatus',
      key: 'reportStatus',
      sorter: true,
      render: (status: string | null) => {
        if (!status) return '-';
        const value = status.toUpperCase();
        return (
          <span className={tableStyles.statusPill} data-status={value}>
            {value}
          </span>
        );
      },
    },
    {
      title: 'Site',
      dataIndex: 'site',
      key: 'site',
      sorter: true,
    },
    {
      title: 'Study Date',
      dataIndex: 'studyDate',
      key: 'studyDate',
      sorter: true,
      render: (text: string) => <span>{formatStudyDateTime(text)}</span>,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (_: string[], record: Study) => (
        <StudyTagsCell study={record} compact={settings.compactMode} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Study) => (
        <div className="row-actions">
          <Space size={8}>
            <Tooltip title="Open full viewer">
              <Button
                type="text"
                icon={<IoEyeOutline />}
                aria-label="Open study"
                onClick={() => {
                  onStudyClick?.(record.uuid);
                  navigate(`/studies/${record.uuid}`);
                }}
              />
            </Tooltip>

            <Tooltip title="Open report only">
              <Button
                type="text"
                icon={<FileTextOutlined />}
                aria-label="Open report"
                onClick={() =>
                  navigate(`/reports/${record.reportUuid}/preview`)
                }
              />
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <Flex vertical gap={24} style={{ width: '100%' }}>
      <Table<Study>
        columns={columns}
        dataSource={studies}
        rowKey={(r) => r.uuid}
        loading={{ spinning: !!loading, indicator: <HeartLoader size="sm" /> }}
        bordered={false}
        onChange={(_pagination, _filters, sorter) => {
          onSortChange?.(sorterToOrdering(sorter));
        }}
        rowClassName={(record) =>
          record.priority === 'STAT' ? tableStyles.priorityRow : ''
        }
        onRow={(record) => ({
          onMouseEnter: () => prefetchStudy(record.uuid),
        })}
        pagination={{
          current: page,
          pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
          onChange: (p, size) => {
            onPageChange?.(p);
            if (size !== pageSize) onPageSizeChange?.(size);
          },
        }}
      />

      {selectedStudy && (
        <StudyPropertiesModal
          open={propertiesOpen}
          onClose={() => setPropertiesOpen(false)}
          studyUuid={selectedStudy.uuid}
          reportUuid={selectedStudy.reportUuid}
        />
      )}
    </Flex>
  );
};

export default StudyTable;
